import React, { useState, useEffect } from 'react';
import TourModal from '../../components/admin/TourModal';

const AdminTours = () => {
    const [tours, setTours] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTour, setEditingTour] = useState(null);
    const [loadingTourDetails, setLoadingTourDetails] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState('');

    const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

    const getAuthHeaders = React.useCallback(() => {
        const token = localStorage.getItem('adminToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }, []);

    const loadTours = React.useCallback(async (catId = '') => {
        try {
            const url = catId
                ? `${API_URL}/api/tours?limit=100&category=${catId}`
                : `${API_URL}/api/tours?limit=100`;
            const response = await fetch(url, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            setTours(data.tours || []);
        } catch (error) {
            console.error('Error loading tours:', error);
        } finally {
            setLoading(false);
        }
    }, [API_URL, getAuthHeaders]);

    const getCategoryName = React.useCallback((categoryId) => {
        if (!categoryId) return 'N/A';
        const cat = categories.find(c => c.id === categoryId || c._id === categoryId);
        return cat ? cat.name : categoryId;
    }, [categories]);

    const loadCategories = React.useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/api/categories`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            setCategories(data);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    }, [API_URL, getAuthHeaders]);

    useEffect(() => {
        loadTours(categoryFilter);
        loadCategories();
    }, [loadTours, loadCategories, categoryFilter]);

    const openModal = async (tour = null) => {
        if (!tour) {
            setEditingTour(null);
            setLoadingTourDetails(false);
            setShowModal(true);
            return;
        }

        const tourId = tour.id || tour._id;
        if (!tourId) {
            alert('Tour ID is missing — cannot load details for editing.');
            return;
        }

        setShowModal(true);
        setLoadingTourDetails(true);
        setEditingTour(null);

        try {
            const response = await fetch(`${API_URL}/api/tours/${tourId}`, {
                headers: getAuthHeaders()
            });

            if (response.ok) {
                const fullTour = await response.json();
                setEditingTour(fullTour);
            } else {
                let message = 'Could not load tour details for editing.';
                try {
                    const err = await response.json();
                    if (err.detail) message = typeof err.detail === 'string' ? err.detail : message;
                } catch (e) {
                    // ignore
                }
                alert(message);
                setShowModal(false);
            }
        } catch (error) {
            console.error('Error loading tour for edit:', error);
            alert('Error loading tour details: ' + error.message);
            setShowModal(false);
        } finally {
            setLoadingTourDetails(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingTour(null);
        setLoadingTourDetails(false);
    };

    const handleSave = async (tourData) => {
        try {
            const url = editingTour
                ? `${API_URL}/api/tours/${editingTour.id}`
                : `${API_URL}/api/tours`;

            const response = await fetch(url, {
                method: editingTour ? 'PUT' : 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(tourData)
            });

            if (response.ok) {
                closeModal();
                loadTours(categoryFilter);
                alert(editingTour ? 'Tour updated successfully!' : 'Tour created successfully!');
            } else {
                let message = 'Failed to save tour';
                try {
                    const error = await response.json();
                    if (typeof error.detail === 'string') {
                        message = error.detail;
                    } else if (Array.isArray(error.detail)) {
                        message = error.detail
                            .map(d => {
                                const field = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : '';
                                return field ? `${field}: ${d.msg}` : d.msg;
                            })
                            .join('\n');
                    } else if (error.detail) {
                        message = JSON.stringify(error.detail);
                    }
                } catch (e) {
                    // ignore parse errors
                }
                alert('Error saving tour:\n' + message);
            }
        } catch (error) {
            console.error('Error saving tour:', error);
            alert('Error saving tour: ' + error.message);
        }
    };

    const deleteTour = async (id) => {
        if (!window.confirm('Are you sure you want to delete this tour?')) return;

        try {
            const response = await fetch(`${API_URL}/api/tours/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                loadTours();
                alert('Tour deleted successfully!');
            }
        } catch (error) {
            console.error('Error deleting tour:', error);
            alert('Error deleting tour');
        }
    };

    const toggleStatus = async (id, currentStatus) => {
        try {
            const response = await fetch(`${API_URL}/api/tours/${id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ isActive: !currentStatus })
            });

            if (response.ok) {
                loadTours();
            }
        } catch (error) {
            console.error('Error toggling status:', error);
        }
    };

    if (loading) {
        return <div className="text-center py-8">Loading...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold">Manage Tours</h2>
                <button
                    onClick={() => openModal()}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
                >
                    <i className="fas fa-plus mr-2"></i>Add New Tour
                </button>
            </div>

            {/* Category Filter */}
            <div className="mb-4 flex items-center gap-3">
                <label className="font-semibold text-gray-700">Filter by Category:</label>
                <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="px-4 py-2 border rounded-lg bg-white shadow-sm"
                >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>
                {categoryFilter && (
                    <button
                        onClick={() => setCategoryFilter('')}
                        className="text-sm text-blue-600 hover:underline"
                    >Clear</button>
                )}
            </div>

            {/* Tours Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {tours.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                    No tours found
                                </td>
                            </tr>
                        ) : (
                            tours.map(tour => (
                                <tr key={tour.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">{tour.title}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {getCategoryName(tour.category)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">₹{tour.price}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                            onClick={() => toggleStatus(tour.id, tour.isActive)}
                                            className={`px-2 py-1 rounded cursor-pointer transition-colors ${tour.isActive
                                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                                                }`}
                                        >
                                            {tour.isActive ? 'Active' : 'Hidden'}
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                            onClick={() => openModal(tour)}
                                            className="text-blue-600 hover:text-blue-800 mr-3"
                                            title="Edit Tour"
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button
                                            onClick={() => deleteTour(tour.id)}
                                            className="text-red-600 hover:text-red-800"
                                            title="Delete Tour"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <TourModal
                show={showModal}
                onClose={closeModal}
                tour={editingTour}
                loadingTour={loadingTourDetails}
                categories={categories}
                onSave={handleSave}
            />
        </div>
    );
};

export default AdminTours;
